/**
 * What the client sees of a service ticket's charges and labor, built in one place for every
 * client surface:
 * - the work order: the staff preview, and the customer's /w/:token page;
 * - both signed payloads: remote signing on the server, kiosk signing in the webapp.
 *
 * Non-printable lines are hidden but billed (Alex, 2026-09-30). They are left out of every line
 * list, but their amounts stay in every subtotal, so the figures the client reads still add up
 * to the total they sign. Sections only group lines; they never price.
 *
 * Each grid lists its unsectioned lines first, then each section by sort order. A section whose
 * lines are all hidden is left out, but its amount still counts in the grid subtotal.
 */

const roundCents = (value) => Math.round(value * 100) / 100;

const lineKey = (row) => String(row.line_item_id ?? row.labor_line_id ?? '');

function byPosition(a, b) {
  return (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || lineKey(a).localeCompare(lineKey(b));
}

function bySectionOrder(a, b) {
  return (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || String(a.section_id).localeCompare(String(b.section_id));
}

function projectGrid(grid, rows, sections, amountOf) {
  const gridSections = sections.filter((section) => section?.grid === grid).sort(bySectionOrder);
  const known = new Set(gridSections.map((section) => section.section_id));
  const ordered = [...rows].sort(byPosition);
  const groupOf = (section) => {
    const members = ordered.filter((row) => (section ? row.section_id === section.section_id : !known.has(row.section_id)));
    return {
      section: section
        ? { section_id: section.section_id, grid, label: section.label ?? '', show_subtotal: Boolean(section.show_subtotal) }
        : null,
      lines: members.filter((row) => !row.non_printable),
      subtotal: roundCents(members.reduce((sum, row) => sum + (Number(amountOf(row)) || 0), 0)),
    };
  };
  return {
    groups: [groupOf(null), ...gridSections.map(groupOf)].filter((group) => group.lines.length > 0),
    subtotal: roundCents(ordered.reduce((sum, row) => sum + (Number(amountOf(row)) || 0), 0)),
  };
}

/**
 * @param {{ lines?: object[] | null, labor?: object[] | null, sections?: object[] | null }} rows
 * @param {{ lineAmount: (row: object) => number, laborAmount: (row: object) => number }} amounts
 */
export function projectServiceWorkOrder({ lines, labor, sections }, { lineAmount, laborAmount }) {
  const allSections = Array.isArray(sections) ? sections : [];
  return {
    charges: projectGrid('charges', Array.isArray(lines) ? lines : [], allSections, lineAmount),
    labor: projectGrid('labor', Array.isArray(labor) ? labor : [], allSections, laborAmount),
  };
}

/** The visible lines of one grid, in work order order, each with its section's label. */
export function serviceWorkOrderLines(grid) {
  return grid.groups.flatMap((group) => group.lines.map((row) => ({ row, section: group.section?.label ?? null })));
}

/** The headings the client sees, with the subtotals they see (null when a section shows none). */
export function serviceWorkOrderSections(projection) {
  return ['charges', 'labor'].flatMap((grid) => projection[grid].groups
    .filter((group) => group.section)
    .map((group) => ({
      grid,
      label: group.section.label,
      subtotal: group.section.show_subtotal ? group.subtotal : null,
    })));
}
